import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { FileUploader } from 'ng2-file-upload';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { environment } from 'src/environments/environment';
import { Constant } from 'src/app/constant/constant';

export interface AttachedFileItem {
  name: string;
  size?: number;
  type?: string;
  serverFileName?: string;
  uploaded: boolean;
  uploading: boolean;
  rawFile?: any;
}

@Component({
  selector: 'app-after-sales-document-upload',
  templateUrl: './after-sales-document-upload.component.html',
  styleUrls: ['./after-sales-document-upload.component.scss']
})
export class AfterSalesDocumentUploadComponent implements OnInit {

  @Input() set existingDocuments(docs: any[] | string | undefined) {
    if (!docs) return;
    let docArray: any[] = [];
    if (typeof docs === 'string') {
      try {
        const parsed = JSON.parse(docs);
        docArray = Array.isArray(parsed) ? parsed : (docs.trim() !== '' ? [docs] : []);
      } catch (e) {
        docArray = docs.trim() !== '' ? [docs] : [];
      }
    } else if (Array.isArray(docs)) {
      docArray = docs;
    }

    docArray.forEach((d: any) => {
      const fileName = typeof d === 'string' ? d : (d?.fileName || d?.name || '');
      if (fileName && !this.fileList.some(f => f.serverFileName === fileName || f.name === fileName)) {
        this.fileList.push({
          name: fileName.split('/').pop() || fileName,
          serverFileName: fileName,
          uploaded: true,
          uploading: false
        });
      }
    });
  }

  @Input() required: boolean = false;
  @Input() uploadType: string = 'stageHistory';
  @Input() maxFileSizeMb: number = 16;

  @Output() documentsChange = new EventEmitter<string[]>();

  @ViewChild('fileInput', { static: false }) fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('previewDialog') previewDialogRef!: TemplateRef<any>;

  public serverPath = environment.commonImageApi + "stageHistory/";
  public locations: string = "calsoftgroup.com";
  public fileList: AttachedFileItem[] = [];
  public hasBaseDropZoneOver = false;
  public uploadInProgress = false;

  public uploader: FileUploader = new FileUploader({
    isHTML5: true
  });

  public cid: string = '';
  public dialogRef: MatDialogRef<any> | null = null;
  public selectedPreviewFile: AttachedFileItem | null = null;
  public selectedPreviewUrl: string = '';
  public selectedPreviewFullUrl: string = '';
  public selectedPreviewSafeUrl: SafeResourceUrl | null = null;

  constructor(
    private fileUploadService: FileUploadService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {

    let values = localStorage.getItem(Constant.enterPriseKey);
    this.cid = "&cid=" + values;
    const maxBytes = (this.maxFileSizeMb || 16) * 1024 * 1024;
    this.uploader.setOptions({
      isHTML5: true,
      maxFileSize: maxBytes
    });

    const allowedExtensions = [
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx',
      'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp',
      'mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm',
      'mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac',
      'txt', 'log', 'rtf'
    ];

    this.uploader.options.filters = this.uploader.options.filters || [];
    this.uploader.options.filters.push({
      name: 'extension',
      fn: (item: any) => {
        const fileName = item.name || '';
        const ext = fileName.split('.').pop()?.toLowerCase() || '';
        return allowedExtensions.includes(ext);
      }
    });

    this.uploader.onWhenAddingFileFailed = (item: any, filter: any, options: any) => {
      let message = '';
      if (filter.name === 'fileSize') {
        message = `File "${item.name}" exceeds the maximum allowed size of ${this.maxFileSizeMb || 16} MB.`;
      } else if (filter.name === 'extension' || filter.name === 'fileType' || filter.name === 'mimeType') {
        message = `File "${item.name}" has an unsupported format. Allowed formats: PDF, DOC, DOCX, XLS, XLSX, PPT, Images, Video, Audio, Text.`;
      } else {
        message = `File "${item.name}" could not be added.`;
      }

      this.snackBar.open(message, '×', {
        panelClass: 'error',
        verticalPosition: 'bottom',
        duration: 4000
      });

      if (this.fileInputRef?.nativeElement) {
        this.fileInputRef.nativeElement.value = '';
      }
    };

    this.uploader.onAfterAddingAll = (fileItems: any[]) => {
      fileItems.forEach(fileItem => {
        fileItem.withCredentials = false;
        const rawFile = fileItem._file || fileItem.file?.rawFile;
        const exists = this.fileList.some(f => f.name === fileItem.file.name && f.size === fileItem.file.size);
        if (!exists) {
          this.fileList.push({
            name: fileItem.file.name,
            size: fileItem.file.size,
            type: fileItem.file.type || rawFile?.type || '',
            rawFile: rawFile || fileItem.file,
            uploaded: false,
            uploading: false,
            serverFileName: ''
          });
        }
      });
      this.uploadPendingFiles();
    };
  }

  fileOverBase(event: boolean): void {
    this.hasBaseDropZoneOver = event;
  }

  uploadPendingFiles(): void {
    const pending = this.fileList.filter(f => !f.uploaded && !f.uploading);
    if (pending.length === 0) return;

    pending.forEach(f => f.uploading = true);
    this.uploadInProgress = true;

    const data = new FormData();
    pending.forEach((f: any) => {
      const sanitizedName = (f.name || 'file')
        .replace(/\s/g, '_')
        .replace(/\(/g, '_')
        .replace(/\)/g, '_');
      data.append('files', f.rawFile, sanitizedName);
    });

    this.fileUploadService.normalUploadFiles(data, this.uploadType, this.locations).subscribe({
      next: (res: any) => {
        this.uploadInProgress = false;
        pending.forEach(f => f.uploading = false);
        this.uploader.clearQueue();

        if (this.fileInputRef?.nativeElement) {
          this.fileInputRef.nativeElement.value = '';
        }

        if (res && res.status_code === '500') {
          this.snackBar.open(res.status_message || 'File upload failed', '×', {
            panelClass: 'error',
            verticalPosition: 'bottom',
            duration: 3000
          });
          return;
        }

        const uploadedGallery: any[] = res?.productEntityMediaGallery || [];
        if (uploadedGallery.length > 0) {
          pending.forEach((f, idx) => {
            const returned = uploadedGallery[idx];
            const serverName = returned?.fileName ?? '';
            if (serverName) {
              f.uploaded = true;
              f.serverFileName = serverName.split('/').pop() || serverName;
            }
          });
          this.emitChanges();
        }
      },
      error: (err) => {
        this.uploadInProgress = false;
        pending.forEach(f => {
          f.uploading = false;
          f.uploaded = false;
        });
        this.snackBar.open('Upload failed. Please try again.', 'Close', { duration: 3000 });
      }
    });
  }

  removeFile(index: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (index >= 0 && index < this.fileList.length) {
      this.fileList.splice(index, 1);
      this.emitChanges();
    }
  }

  removeAllFiles(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.fileList = [];
    this.uploader.clearQueue();
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
    this.emitChanges();
  }

  emitChanges(): void {
    const uploadedFileNames = this.fileList
      .filter(f => f.uploaded && f.serverFileName)
      .map(f => f.serverFileName as string);
    this.documentsChange.emit(uploadedFileNames);
  }

  getFileIcon(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
    if (['mp4', 'avi', 'mov', 'wmv', 'mkv'].includes(ext)) return 'videocam';
    if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audiotrack';
    if (['pdf'].includes(ext)) return 'picture_as_pdf';
    if (['doc', 'docx'].includes(ext)) return 'description';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'table_chart';
    if (['ppt', 'pptx'].includes(ext)) return 'slideshow';
    if (['txt', 'log'].includes(ext)) return 'text_snippet';
    return 'insert_drive_file';
  }

  getFileBadgeClass(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return 'badge-pdf';
    if (['doc', 'docx', 'txt', 'log'].includes(ext)) return 'badge-doc';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'badge-sheet';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'badge-image';
    if (['mp4', 'avi', 'mov', 'wmv', 'mkv'].includes(ext)) return 'badge-video';
    if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'badge-audio';
    return 'badge-other';
  }

  formatFileSize(bytes?: number): string {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  openPreviewDialog(item: AttachedFileItem, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!item.uploaded || !item.serverFileName) return;

    this.selectedPreviewFile = item;
    this.selectedPreviewUrl = this.serverPath + item.serverFileName;
    this.selectedPreviewFullUrl = this.selectedPreviewUrl + (this.cid || '');
    this.selectedPreviewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.selectedPreviewFullUrl);

    this.dialogRef = this.dialog.open(this.previewDialogRef, {
      width: '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'after-sales-preview-dialog'
    });
  }

  closePreviewDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
    this.selectedPreviewFile = null;
    this.selectedPreviewSafeUrl = null;
  }

  isImageFile(fileName?: string): boolean {
    if (!fileName) return false;
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext);
  }

  isPdfFile(fileName?: string): boolean {
    if (!fileName) return false;
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    return ext === 'pdf';
  }

  isVideoFile(fileName?: string): boolean {
    if (!fileName) return false;
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    return ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'].includes(ext);
  }

  isAudioFile(fileName?: string): boolean {
    if (!fileName) return false;
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    return ['mp3', 'wav', 'ogg', 'm4a', 'flac'].includes(ext);
  }
}
